import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 220;
const POPOVER_WIDTH = 360;
const POPOVER_GAP = 6;
const VIEWPORT_MARGIN = 12;

const LinkChainIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5" />
    <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5" />
  </svg>
);

/**
 * Link-chain icon on a task row. Hover (or click to pin) opens a popover with the
 * task's notes and the verbatim source quote; clicking the quote opens the details
 * side panel with that quote highlighted in the original source.
 *
 * @example
 * <ActionItemSourceLink
 *   provenanceLabel="Granola · Oct 29"
 *   noteText="Send Priya the revised deck before Friday"
 *   quotes={["I'll send the deck over by Friday"]}
 *   onOpenQuote={(quote) => openSidebarAtQuote(item, quote)}
 * />
 * // hover → "SOURCE · Granola · Oct 29", the note, and a clickable quote bar
 */
const ActionItemSourceLink = ({
  provenanceLabel = "",
  noteText = "",
  quotes = [],
  onOpenQuote,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleOpen = () => {
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    if (pinned) return;
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  const close = useCallback(() => {
    clearTimer();
    setPinned(false);
    setOpen(false);
  }, []);

  useEffect(() => clearTimer, []);

  // Anchor under the icon, right-aligned, clamped to the viewport.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.right - width, window.innerWidth - width - VIEWPORT_MARGIN)
    );
    const popoverHeight = popoverRef.current?.offsetHeight || 0;
    const fitsBelow =
      rect.bottom + POPOVER_GAP + popoverHeight <= window.innerHeight - VIEWPORT_MARGIN;
    setPosition({
      left,
      width,
      top: fitsBelow
        ? rect.bottom + POPOVER_GAP
        : Math.max(VIEWPORT_MARGIN, rect.top - POPOVER_GAP - popoverHeight),
    });
  }, [open, quotes.length, noteText]);

  // Click outside / Escape closes a pinned popover; scrolling closes any popover
  // (it is anchored with fixed coordinates).
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
    };
  }, [open, close]);

  const quoteList = (Array.isArray(quotes) ? quotes : [])
    .map((q) => String(q || "").trim())
    .filter(Boolean);
  const note = String(noteText || "").trim();

  const openQuote = (quote) => {
    close();
    onOpenQuote?.(quote);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`action-item-source-link-btn action-item-row-action-btn--icon${
          open ? " action-item-source-link-btn--open" : ""
        }`}
        aria-label="Show source"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={scheduleOpen}
        onBlur={scheduleClose}
        onClick={(e) => {
          e.stopPropagation();
          clearTimer();
          if (open && pinned) {
            close();
          } else {
            setPinned(true);
            setOpen(true);
          }
        }}
      >
        <LinkChainIcon />
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-label="Task source"
              className="action-item-source-popover"
              style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                width: position?.width ?? POPOVER_WIDTH,
                visibility: position ? "visible" : "hidden",
              }}
              onMouseEnter={clearTimer}
              onMouseLeave={scheduleClose}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="action-item-source-popover__head">
                <span className="action-item-source-popover__title">Source</span>
                {provenanceLabel ? (
                  <span className="action-item-source-popover__meta">{provenanceLabel}</span>
                ) : null}
              </div>
              {note ? (
                <div className="action-item-source-popover__section">
                  <div className="action-item-source-popover__label">Notes</div>
                  <p className="action-item-source-popover__note">{note}</p>
                </div>
              ) : null}
              {quoteList.length > 0 ? (
                <div className="action-item-source-popover__section">
                  <div className="action-item-source-popover__label">From the source</div>
                  {quoteList.map((quote) => (
                    <button
                      key={quote}
                      type="button"
                      className="action-item-source-popover__quote"
                      onClick={() => openQuote(quote)}
                      title="Open in source"
                    >
                      <span className="action-item-source-popover__quote-text">{quote}</span>
                      <span className="action-item-source-popover__quote-cta">Open in source ›</span>
                    </button>
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  className="action-item-source-popover__open"
                  onClick={() => openQuote("")}
                >
                  View original source ›
                </button>
              )}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default ActionItemSourceLink;
